import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Sidebar } from '@/components/Sidebar';
import type { ChatHistoryItem } from '@/components/Sidebar/ChatHistorySection';
import { useWorkspace } from '@/context';
import { useSpaces } from '@/hooks/useSpaces';
import { getStaticChatHistory } from '@/config/sidebarData';
import { getDeviceSetupConversations } from '@/utils/conversationWorkflow';
import { isBulkUserImportRoute } from '@/utils/isGuidedFlowRoute';

function MainLayout() {
  const { effectiveRole, effectiveUser, workspaceRoute } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const wasBulkImportRef = useRef(false);

  const isBulkUserImport = isBulkUserImportRoute(location.pathname, location.search);

  useEffect(() => {
    if (isBulkUserImport && !wasBulkImportRef.current) {
      setSidebarOpen(false);
    } else if (!isBulkUserImport && wasBulkImportRef.current) {
      setSidebarOpen(true);
    }
    wasBulkImportRef.current = isBulkUserImport;
  }, [isBulkUserImport]);

  const { spaces } = useSpaces(effectiveRole);

  const chatHistory = useMemo<ChatHistoryItem[]>(() => {
    const deviceFlows = getDeviceSetupConversations().map((c) => ({
      id: c.id,
      title: c.title,
    }));

    return [...deviceFlows, ...getStaticChatHistory(effectiveRole)];
  }, [effectiveRole]);

  const handleNewChat = useCallback(() => {
    navigate(workspaceRoute);
  }, [navigate, workspaceRoute]);

  return (
    <div className={`main-layout${sidebarOpen ? '' : ' main-layout--sidebar-collapsed'}`}>
      <Sidebar
        user={effectiveUser}
        spaces={spaces}
        chatHistory={chatHistory}
        isOpen={sidebarOpen}
        onNewChat={handleNewChat}
        onToggle={() => setSidebarOpen((o) => !o)}
      />
      <Topbar
        user={effectiveUser}
        onSidebarToggle={() => setSidebarOpen((o) => !o)}
        sidebarOpen={sidebarOpen}
      />
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

export default MainLayout;
